import {
  memo,
  useCallback,
  useRef,
  useState as classicUseState,
  useState,
} from 'react';
// Side-effect to compile
import { useQuery, useQueryClient } from 'react-query';

import { Portal } from '@mantine/core';
import { ICustomField } from '@models/CustomField.model';
import { indexCustomField } from '@services/queries/customField';
import { InventoryQueryParams } from '@services/queries/equipments/getEquipments/makeParamsObject';
import { EquipmentsMap } from '@store/machines/equipmentsPicker.schema';
import {
  MassEditContext as Context,
  MassEditEvents,
} from '@store/machines/massEdit.schema';
import { FormState } from 'react-hook-form';
import { ExtractEvent } from 'xstate';
import {
  ChildrenT,
  HeadingsT,
  IHeadingChildren,
  initialPages,
  linkHeadingChildren,
  linkHeadings,
} from './helpers/HeadingsCalc';
import LeftSide from './components/LeftSide';
import RightSide, { EditModes } from './components/RightSide';
import { createFromCustomFields } from './helpers/createFromCustomFields';
import { Container, Overlay } from './OnBoardingModal.styles';
import { showOnOnboard } from './OnBoardingModal.utils';
import { prefillCustomFields } from './helpers/prefillCustomFields';
import {
  EquipmentTypeKeepNulls,
  OnboardingControls,
  QueriesToFetch,
} from './types';

type IFormStateOnboarding = FormState<
  Omit<IFormData, 'dirtyFields' | 'isDirty'>
>;
interface IFormData {
  equipments: EquipmentTypeKeepNulls[];
  isDirty: boolean;
  dirtyFields: IFormStateOnboarding['dirtyFields'];
}
const initialChild: ChildrenT = 'Identification';
const initialHeading: HeadingsT = 'Critical Data';
const initObject = initialPages.get(initialHeading)?.children.get(initialChild);
if (!initObject) {
  throw new Error('Failed to find initial Child in pages for mass edit');
}
export interface OnboardingModalProps {
  equipments: EquipmentsMap;
  isOnboarding: boolean;
  onDone: (
    context: Context,
    event: ExtractEvent<MassEditEvents, 'TERMINATE' | 'FORCE-TERMINATE'>,
  ) => void;
  queryParams: Partial<InventoryQueryParams>;
  deselectedItems: string[];
  mode: EditModes;
}
export const ONBORADING_CUSTOM_FIELDS_QUERY_K = [
  'Onboarding',
  'indexCustomField',
];
function OnBoardingModal(props: OnboardingModalProps) {
  const {
    equipments,
    isOnboarding,
    onDone,
    mode,
    queryParams,
    deselectedItems,
  } = props;
  const queryClient = useQueryClient();
  const [pages, setPages] = classicUseState(() =>
    prefillCustomFields(queryClient, initialPages),
  );
  const controlRef = useRef<OnboardingControls>();
  const [fetchedQueries, setFetchedQueries] = useState<QueriesToFetch[]>([]);
  const [activePage, setActivePage] = classicUseState<IHeadingChildren>(
    initObject as IHeadingChildren,
  );
  const customFieldsSelector = useCallback(
    (data: ICustomField[]) => {
      const filteredCustomField = isOnboarding
        ? data.filter(showOnOnboard)
        : data;
      return filteredCustomField;
    },
    [isOnboarding],
  );

  const { data: customFields } = useQuery(
    ONBORADING_CUSTOM_FIELDS_QUERY_K,
    indexCustomField,
    {
      select: customFieldsSelector,
      onSuccess: data => {
        const filteredCustomField = isOnboarding
          ? data.filter(showOnOnboard)
          : data;
        const unlinkedMap = createFromCustomFields(filteredCustomField, pages);
        if (!unlinkedMap) return;
        const linkedHeadings = linkHeadings(unlinkedMap);
        const linkedMap = linkHeadingChildren(linkedHeadings);

        setPages(linkedMap);
        setFetchedQueries(prev => [...prev, 'custom-fields']);
      },
      enabled: !fetchedQueries.includes('custom-fields'),
    },
  );

  controlRef.current = { nextPage, prevPage };
  const definedCustomFields = customFields ?? [];

  return (
    <Portal>
      <Overlay className="no-transitions">
        <Container>
          <LeftSide
            isOnboarding={isOnboarding}
            pages={pages}
            onPageChange={child => setActivePage(child)}
            activePageId={activePage.id}
          />
          <RightSide
            defaultEquipments={equipments}
            isOnboarding={isOnboarding}
            onDone={onDone}
            customFields={definedCustomFields}
            activePage={activePage}
            controlRef={controlRef}
            mode={mode}
            queryParams={queryParams}
            deselectedItems={deselectedItems}
            fetchedQueries={fetchedQueries}
          />
        </Container>
      </Overlay>
    </Portal>
  );
  function nextPage() {
    const parent = pages.get(activePage.parentId);
    if (!parent || !activePage.nextId) return;
    let nextChild = parent.children.get(activePage.nextId);
    if (!nextChild && parent.nextId) {
      const nextParent = pages.get(parent.nextId);
      if (!nextParent) return;
      nextChild = nextParent.children.get(activePage.nextId);
    }
    if (!nextChild) return;
    setActivePage(nextChild);
  }
  function prevPage() {
    const parent = pages.get(activePage.parentId);
    if (!parent || !activePage.prevId) return;
    let prevChild = parent.children.get(activePage.prevId);
    if (!prevChild) {
      if (!parent.prevId) return;
      const parentNext = pages.get(parent.prevId);
      if (!parentNext) return;
      prevChild = parentNext.children.get(activePage.prevId);
    }
    if (!prevChild) return;
    setActivePage(prevChild);
  }
}
export type { IFormData as IFormDataOnboardingModal };
export default memo(OnBoardingModal);
